import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  User,
  FileText,
  Download,
  Utensils,
  Search,
  Filter,
  Calendar,
  Briefcase,
  RefreshCw,
  MapPin,
  Check,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { useValidateData } from '../hooks/useValidateData';
import { useValidateActions } from '../hooks/useValidateActions';
import { TimesheetAuditTrail } from '../components/TimesheetAuditTrail';
import { summarizeMonths, formatMonthFr } from '../utils/sheetStatusUtils';
import { formatDiversEntryLabel } from '../utils/diversDisplayUtils';

const ZONE_OPTIONS: { value: string; label: string }[] = [
  { value: '',   label: 'Aucune' },
  { value: '1A', label: 'Z1A' },
  { value: '1B', label: 'Z1B' },
  { value: '2',  label: 'Z2' },
  { value: '3',  label: 'Z3' },
  { value: '4',  label: 'Z4' },
  { value: '5',  label: 'Z5' },
  { value: '6',  label: 'Z6' },
  { value: '7',  label: 'Z7' },
  { value: 'GD', label: 'ZGD' },
];

export default function AdminValidate() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'pending' | 'in_progress'>('pending');

  const {
    selectedSheetId,
    setSelectedSheetId,
    entries,
    setEntries,
    loadSheetDetails,
    currentSheet,
    selectedSheetNotFound,
    detailLoading,
    projects,
    pendingSheets,
    inProgressSheets,
    refreshInProgress,
    loading,
    refreshSheets,
    overrideLoading,
    setOverrideLoading,
    zoneSavedEntryId,
    setZoneSavedEntryId,
    savedFieldId,
    setSavedFieldId,
    filterName,
    setFilterName,
    filterWeek,
    setFilterWeek,
    filterMonth,
    setFilterMonth,
    filterProject,
    setFilterProject,
    filteredSheets,
    totalHours,
    totalMeals,
    totalTransport,
    totalTrajet,
    totalGDDays,
    normalHoursTotal,
    saturdayHoursTotal,
    sundayHoursTotal,
    hasGD,
    overtimeBreakdown,
  } = useValidateData();

  const {
    handleValidate,
    handleValidateMonth,
    handleReject,
    handleOverrideChange,
    handleZoneChange,
    handleApplyZoneToAll,
    handleIndemnityToggle,
    regeneratePdf,
  } = useValidateActions({
    selectedSheetId,
    setSelectedSheetId,
    currentSheet,
    entries,
    setEntries,
    pendingSheets,
    refreshSheets,
    refreshInProgress,
    loadSheetDetails,
    setOverrideLoading,
    setZoneSavedEntryId,
    setSavedFieldId,
    normalHoursTotal,
    saturdayHoursTotal,
    sundayHoursTotal,
    hasGD,
  });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth:'1600px', margin:'0 auto', padding:'20px' }}>

      {/* Header */}
      <PageHeader
        title={selectedSheetId ? 'Détail Feuille' : 'Validation des Heures'}
        onBack={() => selectedSheetId ? setSelectedSheetId(null) : navigate('/')}
      />

      {/* FILTRES (Liste Principale) */}
      {!selectedSheetId && (
        <Card style={{ marginBottom: '24px', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input type="text" placeholder="Nom employé..." value={filterName} onChange={(e) => setFilterName(e.target.value)} style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '0 12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <Calendar size={16} color="#64748b" />
            <select value={filterWeek} onChange={(e) => { setFilterWeek(e.target.value); setFilterMonth(''); }} style={{ background: 'transparent', border: 'none', fontSize: '0.9rem', color: '#334155', padding: '10px 0', cursor: 'pointer', outline: 'none', minWidth: '110px' }}>
              <option value="all">Toutes sem.</option>
              {Array.from({ length: 53 }, (_, i) => i + 1).map(w => <option key={w} value={w}>Semaine {w}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '0 12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <Filter size={16} color="#64748b" />
            <input type="month" value={filterMonth} onChange={(e) => { setFilterMonth(e.target.value); setFilterWeek('all'); }} style={{ background: 'transparent', border: 'none', fontSize: '0.9rem', color: '#334155', padding: '9px 0', cursor: 'pointer', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '0 12px', borderRadius: '10px', border: '1px solid #e2e8f0', flex: 1.5, minWidth:'250px' }}>
            <Briefcase size={16} color="#64748b" />
            <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} style={{ width:'100%', background: 'transparent', border: 'none', fontSize: '0.9rem', color: '#334155', padding: '10px 0', cursor: 'pointer', outline: 'none' }}>
              <option value="all">Tous les chantiers</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
            </select>
          </div>
          {(filterName || filterWeek !== 'all' || filterMonth || filterProject !== 'all') && (
            <button onClick={() => { setFilterName(''); setFilterWeek('all'); setFilterMonth(''); setFilterProject('all'); }} style={{ fontSize:'0.85rem', color:'#ef4444', background:'transparent', border:'none', cursor:'pointer', fontWeight:'600' }}>Effacer</button>
          )}
        </Card>
      )}

      {loading && <LoadingSpinner />}

      {/* ONGLETS : À valider (pending) / En cours (draft + 1+ validated) */}
      {!selectedSheetId && !loading && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
          <button
            onClick={() => setActiveTab('pending')}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'pending' ? '3px solid #2563eb' : '3px solid transparent',
              color: activeTab === 'pending' ? '#2563eb' : '#64748b',
              fontWeight: '600',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}>
            À valider <span style={{ marginLeft: '6px', background: activeTab === 'pending' ? '#dbeafe' : '#f1f5f9', color: activeTab === 'pending' ? '#1e40af' : '#64748b', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem' }}>{filteredSheets.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('in_progress')}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'in_progress' ? '3px solid #2563eb' : '3px solid transparent',
              color: activeTab === 'in_progress' ? '#2563eb' : '#64748b',
              fontWeight: '600',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}>
            En cours <span style={{ marginLeft: '6px', background: activeTab === 'in_progress' ? '#dbeafe' : '#f1f5f9', color: activeTab === 'in_progress' ? '#1e40af' : '#64748b', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem' }}>{inProgressSheets.length}</span>
          </button>
        </div>
      )}

      {/* LISTE À VALIDER (status=pending, au moins 1 jour submitted) */}
      {!selectedSheetId && !loading && activeTab === 'pending' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {filteredSheets.length === 0 ? (
            <div style={{ gridColumn: '1/-1' }}>
              <EmptyState
                message={pendingSheets.length === 0 ? "Aucune feuille à valider" : "Aucune feuille ne correspond aux filtres."}
                icon={<CheckCircle size={48} />}
              />
            </div>
          ) : filteredSheets.map(sheet => (
            <div key={sheet.id} onClick={() => loadSheetDetails(sheet.id)} style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px', overflow: 'hidden' }}>
                <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '50%', color: '#2563eb', flexShrink: 0 }}><User size={20} /></div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div title={sheet.employee?.full_name || 'Inconnu'} style={{ fontWeight: '700', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sheet.employee?.full_name || 'Inconnu'}</div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Semaine {sheet.week_number} • {sheet.year}</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '15px', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap' }}>Signé le {sheet.signed_at ? new Date(sheet.signed_at).toLocaleDateString() : 'Date inc.'}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {sheet.override_weekly_hours != null && (
                    <span style={{ background: '#fff7ed', color: '#c2410c', padding: '4px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '700', border: '1px solid #fed7aa' }}>
                      Base: {sheet.override_weekly_hours}h
                    </span>
                  )}
                  {sheet.pdf_url && <div title="PDF Disponible" style={{ color: '#6366f1', background:'#eef2ff', padding:'4px', borderRadius:'6px' }}><FileText size={16} /></div>}
                  <span style={{ background: '#fef3c7', color: '#b45309', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700' }}>EN ATTENTE</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LISTE EN COURS (cheval partielle : 1 mois validé, autre encore à saisir) */}
      {!selectedSheetId && !loading && activeTab === 'in_progress' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {inProgressSheets.length === 0 ? (
            <div style={{ gridColumn: '1/-1' }}>
              <EmptyState
                message="Aucune feuille en cours de saisie côté employé."
                icon={<CheckCircle size={48} />}
              />
            </div>
          ) : inProgressSheets.map(sheet => {
            const summary = summarizeMonths(sheet.day_statuses ?? {});
            const validatedMonths = summary.filter(m => m.validated > 0 && m.draft === 0 && m.submitted === 0);
            const draftMonths = summary.filter(m => m.draft > 0);
            return (
              <div key={sheet.id} onClick={() => loadSheetDetails(sheet.id)} style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px', overflow: 'hidden' }}>
                  <div style={{ background: '#ecfdf5', padding: '10px', borderRadius: '50%', color: '#10b981', flexShrink: 0 }}><User size={20} /></div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div title={sheet.employee?.full_name || 'Inconnu'} style={{ fontWeight: '700', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sheet.employee?.full_name || 'Inconnu'}</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Semaine {sheet.week_number} • {sheet.year}</div>
                  </div>
                </div>
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', fontSize: '0.8rem', color: '#64748b', lineHeight: '1.5' }}>
                  {validatedMonths.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981' }}>
                      <Check size={14} strokeWidth={2.5} />
                      <span>Validé : {validatedMonths.map(m => formatMonthFr(m.monthKey)).join(', ')}</span>
                    </div>
                  )}
                  {draftMonths.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#b45309', marginTop: '4px' }}>
                      <Clock size={14} strokeWidth={2.5} />
                      <span>En attente : {draftMonths.map(m => `${formatMonthFr(m.monthKey)} (${m.draft}j)`).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DÉTAIL — État 1 : loading */}
      {selectedSheetId && detailLoading && !currentSheet && (
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <LoadingSpinner />
          <div style={{ color: '#64748b', marginTop: '12px' }}>Chargement de la feuille…</div>
        </Card>
      )}

      {/* DÉTAIL — État 2 : feuille introuvable (fix anti-page-blanche, incident CORDIER S19) */}
      {selectedSheetId && !detailLoading && (selectedSheetNotFound || !currentSheet) && (
        <Card style={{ padding: '40px 24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center' }}>
            <div style={{ background: '#fee2e2', borderRadius: '50%', padding: '16px', color: '#dc2626' }}>
              <AlertTriangle size={32} />
            </div>
            <h2 style={{ margin: 0, color: '#991b1b' }}>Feuille introuvable</h2>
            <p style={{ margin: 0, color: '#64748b', maxWidth: '480px', lineHeight: 1.5 }}>
              Cette feuille n'a pas pu être chargée. Soit elle n'existe plus, soit elle a
              changé de statut depuis votre dernière action (validation, rejet, correction).
              Revenez à la liste pour voir l'état à jour.
            </p>
            <button
              onClick={() => setSelectedSheetId(null)}
              style={{ padding: '12px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer' }}
            >
              Retour à la liste
            </button>
          </div>
        </Card>
      )}

      {/* DÉTAIL — État 3 : feuille chargée */}
      {selectedSheetId && !detailLoading && currentSheet && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>

          {/* GAUCHE : TABLEAU DES HEURES (CORRIGÉ) */}
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', gridColumn: 'span 2' }}>

            {/* BARRE GLOBALE : Appliquer une zone à toutes les entrées */}
            <div style={{
              padding: '10px 16px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.85rem',
            }}>
              <MapPin size={15} color="#64748b" />
              <span style={{ color: '#64748b', fontWeight: '600' }}>Zone globale :</span>
              <select
                value=""
                onChange={(ev) => {
                  const val = ev.target.value;
                  if (val !== '') handleApplyZoneToAll(val === '__none__' ? '' : val);
                  ev.target.value = ''; // Reset pour réafficher le placeholder
                }}
                style={{
                  padding: '5px 10px',
                  borderRadius: '8px',
                  border: '1.5px solid #cbd5e1',
                  background: 'white',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#334155',
                  cursor: 'pointer',
                }}
              >
                <option value="" disabled>Appliquer à toutes les lignes...</option>
                {ZONE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value === '' ? '__none__' : opt.value}>{opt.label}</option>
                ))}
              </select>
              {zoneSavedEntryId === '__all__' && (
                <span style={{
                  background: '#dcfce7',
                  color: '#16a34a',
                  padding: '3px 10px',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                }}>
                  ✓ Appliqué
                </span>
              )}
            </div>

            {/* WARNING corruption — feuille marquée pending/signed mais 0 entries
                (incident CORDIER S17/S19) : bloque la validation côté admin.
                L'employé doit ressaisir après reset par l'admin. */}
            {entries.length === 0 && currentSheet.total_hours > 0 && (
              <div style={{ margin: '12px 16px', padding: '14px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <AlertTriangle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '0.9rem', color: '#7f1d1d', lineHeight: 1.5 }}>
                  <strong>Feuille corrompue détectée.</strong> La feuille déclare {currentSheet.total_hours}h
                  mais aucune saisie n'est en base. Le PDF signé reste consultable, mais la validation
                  est bloquée. Reset la feuille en draft pour permettre la ressaisie par l'employé.
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <tr>
                    <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '0.85rem' }}>Date</th>
                    <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '0.85rem' }}>Projet</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>Heures</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>Zone</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>P. Repas</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>Transp.</th>
                    <th style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>Trajet</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <FileText size={32} color="#cbd5e1" />
                          <div style={{ fontWeight: 600 }}>Aucune saisie sur cette feuille</div>
                          <div style={{ fontSize: '0.85rem' }}>
                            {currentSheet.total_hours > 0
                              ? 'La feuille déclare des heures mais aucune ligne n\'est en base — corruption détectée.'
                              : 'L\'employé n\'a rien saisi cette semaine.'}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {entries.map((e, index) => {
                    // Logique de regroupement par jour
                    const isFirstOfDay = index === 0 || entries[index - 1].work_date !== e.work_date;
                    const isLastOfDay = index === entries.length - 1 || entries[index + 1].work_date !== e.work_date;

                    // Bordure plus épaisse pour séparer les jours
                    const borderStyle = isLastOfDay ? '2px solid #e2e8f0' : '1px solid #f1f5f9';

                    // Déterminer si c'est un jour GD
                    const isGDDay = e.is_grand_deplacement === true;

                    return (
                      <tr key={e.id} style={{ borderBottom: borderStyle, background: isGDDay ? '#fef2f2' : 'transparent' }}>
                        {/* Date affichée une seule fois */}
                        <td style={{ padding: '16px', fontWeight: '500', color: isFirstOfDay ? '#1e293b' : 'transparent' }}>
                          {isFirstOfDay ? new Date(e.work_date).toLocaleDateString() : ''}
                        </td>

                        <td style={{ padding: '16px', color: '#475569' }}>
                          {/* Affichage prioritaire : si on a le join divers_sub_project
                              (nouvelle colonne divers_sub_project_id, mai 2026), on
                              utilise son nom — c'est la source structurée et fiable.
                              Sinon fallback sur la description (legacy `[GP] FIMOR`
                              ou `26E0259 - FIMOR` posée par les anciens builds). */}
                          {e.divers_sub_project
                            ? `${e.project?.code ?? '???'} - ${e.divers_sub_project.name}`
                            : (e.project?.category === 'divers' && e.description
                                ? formatDiversEntryLabel(e.project?.code, e.description)
                                : `${e.project?.code} - ${e.project?.name}`)}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center', fontWeight: '700', color: '#2563eb' }}>
                          {(e.hours_normal||0) + (e.hours_saturday||0) + (e.hours_major||0)}
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                          {isFirstOfDay ? (
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <select
                                value={
                                  isGDDay ? 'GD' :
                                  (e.travel_zone && e.travel_zone.toString().toUpperCase() !== 'GD')
                                    ? e.travel_zone.toString()
                                    : ''
                                }
                                onChange={(ev) => handleZoneChange(e.id, ev.target.value)}
                                style={{
                                  padding: '5px 8px',
                                  borderRadius: '8px',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  border: isGDDay ? '1.5px solid #fca5a5' : '1.5px solid #cbd5e1',
                                  background: isGDDay ? '#fef2f2' : 'white',
                                  color: isGDDay ? '#dc2626' : '#334155',
                                  cursor: 'pointer',
                                  outline: 'none',
                                  minWidth: '76px',
                                  appearance: 'auto',
                                }}
                              >
                                {ZONE_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              {zoneSavedEntryId === e.id && (
                                <span style={{
                                  position: 'absolute', top: '-6px', right: '-6px',
                                  background: '#16a34a', color: 'white',
                                  width: '16px', height: '16px', borderRadius: '50%',
                                  fontSize: '0.65rem', fontWeight: '700',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>✓</span>
                              )}
                            </div>
                          ) : ''}
                        </td>

                        {/* Paniers / Transport / Trajet — éditable à la volée */}
                        {/* RÈGLE GD : Les jours GD n'ont pas de panier/transport/trajet */}
                        {(['meals_count', 'indemnity_transport', 'indemnity_trajet'] as const).map(field => (
                          <td key={field} style={{ padding: '8px 4px', textAlign: 'center' }}>
                            {isFirstOfDay ? (isGDDay ? (
                              <span style={{ color: '#94a3b8' }}>-</span>
                            ) : (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <div
                                  onClick={() => handleIndemnityToggle(e.id, field, e[field] ? 0 : 1)}
                                  style={{
                                    width: '24px', height: '24px', borderRadius: '6px',
                                    border: `2px solid ${e[field] ? '#16a34a' : '#cbd5e1'}`,
                                    background: e[field] ? '#dcfce7' : 'white',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', margin: '0 auto', transition: 'all 0.15s'
                                  }}
                                >
                                  {e[field] ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '0.75rem' }}>✓</span> : null}
                                </div>
                                {savedFieldId === `${e.id}-${field}` && (
                                  <span style={{
                                    position: 'absolute', top: '-6px', right: '-6px',
                                    background: '#16a34a', color: 'white', width: '14px', height: '14px',
                                    borderRadius: '50%', fontSize: '0.6rem', fontWeight: 700,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>✓</span>
                                )}
                              </div>
                            )) : null}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* DROITE : RÉSUMÉ & ACTIONS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ marginTop: 0, fontSize: '1rem', color: '#64748b' }}>Synthèse Semaine</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b', marginBottom:'15px' }}>{totalHours} <span style={{fontSize:'1rem', color:'#94a3b8'}}>heures</span></div>

              {/* Override base horaire — visible si intérimaire ou custom hours */}
              {(currentSheet.employee?.is_interim || currentSheet.employee?.custom_weekly_hours != null) && (
                <div style={{ background: currentSheet.override_weekly_hours != null ? '#fff7ed' : '#f8fafc', border: `1px solid ${currentSheet.override_weekly_hours != null ? '#fed7aa' : '#e2e8f0'}`, borderRadius: '12px', padding: '12px 15px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: currentSheet.override_weekly_hours != null ? '#c2410c' : '#475569' }}>
                      Base horaire hebdo
                    </div>
                    <select
                      value={currentSheet.override_weekly_hours != null ? String(currentSheet.override_weekly_hours) : ''}
                      onChange={(e) => handleOverrideChange(e.target.value)}
                      disabled={overrideLoading}
                      style={{
                        padding: '6px 10px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600',
                        border: `1px solid ${currentSheet.override_weekly_hours != null ? '#fb923c' : '#cbd5e1'}`,
                        background: currentSheet.override_weekly_hours != null ? '#fff7ed' : 'white',
                        color: currentSheet.override_weekly_hours != null ? '#c2410c' : '#334155',
                        cursor: overrideLoading ? 'wait' : 'pointer', outline: 'none', minWidth: '160px'
                      }}
                    >
                      <option value="">Par défaut ({currentSheet.employee?.custom_weekly_hours ?? '32/38'}h)</option>
                      <option value="32">32h</option>
                      <option value="35">35h</option>
                      <option value="37">37h</option>
                      <option value="38">38h</option>
                      <option value="39">39h</option>
                      <option value="40">40h</option>
                    </select>
                  </div>
                  {currentSheet.override_weekly_hours != null && (
                    <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#ea580c', fontStyle: 'italic' }}>
                      Base modifiée pour cette semaine uniquement
                    </div>
                  )}
                </div>
              )}

              {/* Section Heures Supplémentaires */}
              {overtimeBreakdown && (overtimeBreakdown.overtime25 > 0 || overtimeBreakdown.overtime50 > 0 || overtimeBreakdown.sundayHours > 0) && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '12px', padding: '15px', marginBottom: '15px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#92400e', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Heures Supplémentaires
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {overtimeBreakdown.overtime25 > 0 && (
                      <span style={{ background: '#fffbeb', color: '#b45309', padding: '4px 10px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600' }}>
                        {overtimeBreakdown.overtime25.toFixed(2)}h à 25%
                      </span>
                    )}
                    {overtimeBreakdown.overtime50 > 0 && (
                      <span style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 10px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600' }}>
                        {overtimeBreakdown.overtime50.toFixed(2)}h à 50%
                      </span>
                    )}
                    {overtimeBreakdown.sundayHours > 0 && (
                      <span style={{ background: '#dbeafe', color: '#1e40af', padding: '4px 10px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600' }}>
                        {overtimeBreakdown.sundayHours.toFixed(2)}h dim. (100%)
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#78716c' }}>
                    Heures normales : {overtimeBreakdown.regularHours.toFixed(2)}h | Seuil : {overtimeBreakdown.weeklyThreshold}h ({overtimeBreakdown.isEvenWeek ? 'paire' : 'impaire'})
                  </div>
                </div>
              )}

              {/* Section Grand Déplacement */}
              {totalGDDays > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '15px', marginBottom: '15px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#dc2626', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Grand Déplacement
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#991b1b' }}>
                    <strong>{totalGDDays}</strong> jour{totalGDDays > 1 ? 's' : ''} GD
                  </div>
                </div>
              )}

              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '15px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#475569' }}>
                  <Utensils size={14} className="text-orange-500" />
                  <span><strong>{totalMeals}</strong> Repas</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#475569' }}>
                  <div style={{width:6, height:6, background:'#3b82f6', borderRadius:'50%'}}></div>
                  <span><strong>{totalTransport}</strong> Transp.</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#475569' }}>
                  <div style={{width:6, height:6, background:'#10b981', borderRadius:'50%'}}></div>
                  <span><strong>{totalTrajet}</strong> Trajets</span>
                </div>
              </div>

              {currentSheet.pdf_url ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
                  <a href={currentSheet.pdf_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#eff6ff', color: '#2563eb', borderRadius: '8px', textDecoration: 'none', fontWeight: '600', border: '1px solid #dbeafe', transition: 'background 0.2s' }}>
                    <Download size={18} /> Télécharger le PDF
                  </a>
                  <button onClick={regeneratePdf} disabled={overrideLoading} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px', background: '#fff7ed', color: '#c2410c', borderRadius: '8px',
                    fontWeight: '600', border: '1px solid #fed7aa', cursor: overrideLoading ? 'wait' : 'pointer',
                    opacity: overrideLoading ? 0.6 : 1, transition: 'background 0.2s', fontSize: '0.9rem'
                  }}>
                    <RefreshCw size={18} style={overrideLoading ? { animation: 'spin 1s linear infinite' } : {}} /> {overrideLoading ? 'Régénération...' : 'Régénérer le PDF'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
                  <div style={{ padding: '12px', background: '#f1f5f9', color: '#64748b', borderRadius: '8px', textAlign:'center', fontSize:'0.9rem', fontStyle:'italic' }}>Aucun PDF disponible</div>
                  <button onClick={regeneratePdf} disabled={overrideLoading} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px', background: '#fff7ed', color: '#c2410c', borderRadius: '8px',
                    fontWeight: '600', border: '1px solid #fed7aa', cursor: overrideLoading ? 'wait' : 'pointer',
                    opacity: overrideLoading ? 0.6 : 1, transition: 'background 0.2s', fontSize: '0.9rem'
                  }}>
                    <RefreshCw size={18} style={overrideLoading ? { animation: 'spin 1s linear infinite' } : {}} /> {overrideLoading ? 'Génération...' : 'Générer le PDF'}
                  </button>
                </div>
              )}

              <div style={{ marginTop: '20px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '10px' }}>Signature Employé :</div>
                {currentSheet.signature_url ? (
                  <img src={currentSheet.signature_url} alt="Signature" style={{ width: '100%', border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '10px' }} />
                ) : <span style={{ color: '#ef4444' }}>Manquante</span>}
              </div>
            </div>

            {/* Historique des modifications */}
            {selectedSheetId && (
              <TimesheetAuditTrail sheetId={selectedSheetId} compact={true} />
            )}

            {/*
              VALIDATION — support semaine à cheval.
              On calcule le résumé par mois depuis day_statuses. Cas :
              - 1 seul mois avec des jours 'submitted' → bouton "Valider" simple
              - 2 mois avec des submitted → "Valider mars" + "Valider avril" + "Valider tout"
              - 0 submitted (que des validated/draft) → pas de bouton (rien à valider)
            */}
            {(() => {
              const summary = summarizeMonths(currentSheet.day_statuses ?? {});
              const monthsWithSubmitted = summary.filter((m) => m.submitted > 0);
              const hasAnySubmitted = monthsWithSubmitted.length > 0;
              const isStraddle = summary.length > 1;
              // Garde anti-validation feuille vide : pour éviter de valider une
              // feuille corrompue (status pending + 0 entries) qui retomberait
              // ensuite en analytics / paie comme "officielle" sans données.
              const isCorrupted = entries.length === 0 && currentSheet.total_hours > 0;
              const canValidate = hasAnySubmitted && !isCorrupted;

              return (
                <>
                  {isStraddle && hasAnySubmitted && (
                    <div style={{ padding: '12px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', fontSize: '0.85rem', color: '#1e40af', marginBottom: '4px' }}>
                      Semaine à cheval. Vous pouvez valider mois par mois : les jours validés seront figés, les jours non soumis restent éditables pour l'employé.
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <button onClick={handleReject} style={{ padding: '16px', background: '#fff', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <XCircle size={20} /> Rejeter
                    </button>
                    {isCorrupted ? (
                      <button disabled title="Feuille corrompue (0 saisies en base) : reset à draft requis avant validation" style={{ padding: '16px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '12px', cursor: 'not-allowed', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <AlertTriangle size={18} /> Validation bloquée
                      </button>
                    ) : isStraddle && monthsWithSubmitted.length === 1 ? (
                      <button onClick={() => handleValidateMonth(monthsWithSubmitted[0].monthKey)} style={{ padding: '16px', background: '#10b981', border: 'none', color: 'white', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <CheckCircle size={20} /> Valider {formatMonthFr(monthsWithSubmitted[0].monthKey)} ({monthsWithSubmitted[0].submitted}j)
                      </button>
                    ) : canValidate ? (
                      <button onClick={handleValidate} style={{ padding: '16px', background: '#10b981', border: 'none', color: 'white', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <CheckCircle size={20} /> {isStraddle ? 'Valider tout' : 'Valider'}
                      </button>
                    ) : (
                      <button disabled style={{ padding: '16px', background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#94a3b8', borderRadius: '12px', cursor: 'not-allowed', fontWeight: '600' }}>
                        Aucun jour à valider
                      </button>
                    )}
                  </div>
                  {!isCorrupted && isStraddle && monthsWithSubmitted.length >= 2 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
                      {monthsWithSubmitted.map((m) => (
                        <button key={m.monthKey} onClick={() => handleValidateMonth(m.monthKey)} style={{ padding: '12px', background: '#fff', border: '1px solid #10b981', color: '#10b981', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <CheckCircle size={16} /> Valider {formatMonthFr(m.monthKey)} ({m.submitted}j)
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
